import type { Status } from "../store/admin/statusesStore";
import type { TFunction } from "i18next";

// Map of status IDs to canonical names (handles migration from numeric/string to full IDs)
export const STATUS_ID_MAP: Record<string, string> = {
  // ACTIVE
  "8": "ACTIVE",
  R5tW0uX4yY9cF6dH2aI8: "ACTIVE",
  // INACTIVE
  "9": "INACTIVE",
  S6uX1vY5zZ0dG7eI3bJ9: "INACTIVE",
  // NO_DATA
  "29": "NO_DATA",
  aygay92Qy5GJAuBLhnOq: "NO_DATA",
  BzB1YLslaicNF6TrA1U6: "NO_DATA",
};

// Helper to normalize status ID to canonical name
export function normalizeStatusId(statusId: string): string | null {
  return STATUS_ID_MAP[statusId] || null;
}

export const ENTITY_STATUS_CONFIG: Record<string, string[]> = {
  devices: ["ACTIVE", "INACTIVE"],
  groups: ["ACTIVE", "INACTIVE"],
  trailers: ["ACTIVE", "INACTIVE", "MAINTENANCE", "OUT_OF_SERVICE"],
  units: ["ACTIVE", "INACTIVE"],
  routes: ["ACTIVE", "INACTIVE", "MAINTENANCE"],
  trips: ["ACTIVE", "INACTIVE"],
  pointsOfInterest: ["ACTIVE", "INACTIVE"],
  geofences: ["ACTIVE", "INACTIVE"],
  users: ["ACTIVE", "INACTIVE"],
  drivers: ["ACTIVE", "INACTIVE"],
};

// Geofence types configuration
export const GEOFENCE_TYPE_CONFIG = [
  "warehouse",
  "restrictedArea",
  "customer",
  "route",
  "maintenance",
  "authorizedStop",
];

export function getGeofenceTypeLabel(type: string, t?: TFunction): string {
  if (t) {
    return t(`geofences.types.${type}`, type);
  }
  return type;
}

export function getGeofenceTypeOptions(
  t?: TFunction,
): { value: string; label: string }[] {
  return GEOFENCE_TYPE_CONFIG.map((type) => ({
    value: type,
    label: getGeofenceTypeLabel(type, t),
  }));
}

export function getStatusHumanLabel(statusName: string, t?: TFunction): string {
  if (!statusName) return "";
  if (t) {
    return t(`statuses.${statusName}`, statusName);
  }
  return statusName;
}

// ---------------------------------------------------------------------------
// Status badge helpers (shared across routes, trips, devices, etc.)
// ---------------------------------------------------------------------------

export interface StatusLike {
  id?: string;
  name?: string;
  color?: string | null;
}

// Tailwind-compiled classes per canonical status name. Keep keys uppercase.
const STATUS_BADGE_CLASSES: Record<string, string> = {
  ACTIVE:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  INACTIVE: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  MAINTENANCE:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  OUT_OF_SERVICE:
    "bg-gray-200 text-gray-800 dark:bg-gray-800/40 dark:text-gray-300",
  NO_DATA: "bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400",
  ON_TRIP: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  PAUSED:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  SUSPENDED: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const FALLBACK_BADGE_CLASS =
  "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400";

// Resolve canonical name from a Status-like object (status object first,
// then by ID via STATUS_ID_MAP).
export function resolveCanonicalStatusName(
  status?: StatusLike | null,
): string | null {
  if (!status) return null;
  if (status.name) return status.name.toUpperCase().trim();
  if (status.id) return normalizeStatusId(status.id);
  return null;
}

// Returns the tailwind class string for the status badge. For unknown
// statuses with a custom color, returns "" — caller should fall back to
// `getStatusBadgeStyle` (inline style) since dynamic Tailwind classes don't
// compile via JIT.
export function getStatusBadgeClass(status?: StatusLike | null): string {
  const canonical = resolveCanonicalStatusName(status);
  if (canonical && STATUS_BADGE_CLASSES[canonical]) {
    return STATUS_BADGE_CLASSES[canonical];
  }
  if (status?.color) return "";
  return FALLBACK_BADGE_CLASS;
}

// Inline style for custom statuses with a backend-provided color. Returns
// undefined when a Tailwind class already covers the status.
export function getStatusBadgeStyle(
  status?: StatusLike | null,
): { backgroundColor: string; color: string } | undefined {
  const canonical = resolveCanonicalStatusName(status);
  if (canonical && STATUS_BADGE_CLASSES[canonical]) return undefined;
  if (status?.color) {
    return { backgroundColor: `${status.color}20`, color: status.color };
  }
  return undefined;
}

// Returns the localized text for a status, preferring canonical translation
// keys and falling back to the raw name.
export function getStatusBadgeLabel(
  status?: StatusLike | null,
  t?: TFunction,
  fallback = "",
): string {
  const canonical = resolveCanonicalStatusName(status);
  if (canonical) return getStatusHumanLabel(canonical, t);
  if (status?.name) return getStatusHumanLabel(status.name, t);
  return fallback;
}

export type EntityType = keyof typeof ENTITY_STATUS_CONFIG;

export function getStatusesForEntity(
  statuses: Status[] | undefined | null,
  entity: EntityType,
  companyId?: string | null,
): Status[] {
  // Defensa: si el caller no pasó statuses (store aún cargando o prop
  // olvidada) devolvemos lista vacía en vez de romper con
  // `Cannot read properties of undefined (reading 'filter')`.
  if (!Array.isArray(statuses)) return [];

  const allowedNames = ENTITY_STATUS_CONFIG[entity];
  if (!allowedNames) return statuses;

  const normalizedNames = allowedNames.map((n) => n.toLowerCase().trim());

  return statuses.filter((status) => {
    // Skip statuses with invalid name
    if (!status.name) return false;

    // Check if status ID maps to an allowed canonical name
    const canonicalName = normalizeStatusId(status.id);
    const isAllowedById =
      canonicalName && normalizedNames.includes(canonicalName.toLowerCase());

    // Include default statuses (matching allowed names by name)
    const isDefaultStatus = normalizedNames.includes(
      status.name.toLowerCase().trim(),
    );

    // Include custom statuses for the user's company
    const isCustomStatusForCompany =
      companyId && status.company_id === companyId;

    return isAllowedById || isDefaultStatus || isCustomStatusForCompany;
  });
}

export function getStatusOptionsForEntity(
  statuses: Status[] | undefined | null,
  entity: EntityType,
  t?: TFunction,
  companyId?: string | null,
): { value: string; label: string }[] {
  return getStatusesForEntity(statuses, entity, companyId).map((status) => {
    // Normalize status ID to get canonical name for proper translation
    const canonicalName = normalizeStatusId(status.id) || status.name;
    return {
      value: status.id,
      label: getStatusHumanLabel(canonicalName, t),
    };
  });
}
